import get from 'lodash.get';
import moment from 'moment';

import {
    VirtualizedTableItem,
    VirtualizedTableColumns
} from 'services/virtualizedTable/types';

// @ts-ignore
import { selectActiveTableCellRowIds } from 'services/selectedTableCells/selectors';

// Selectors
import {
    selectFilteredSprayEventsArray,
    selectSprayEvents
} from 'services/sprayEvents/selectors';
import { selectEntities } from 'services/entities/selectors';
import {
    selectSpraySchemaEntityTypeName,
    selectSpraySchemaInnerEntityTypePluralName
} from 'services/schemas/selectors';

// @ts-ignore
import { selectPeopleKeyedById } from 'services/people/selectors';
import { Person } from 'services/people/types';

import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatDropdownOptions from 'utils/format-dropdown-options';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_CONFIRMATION_CHECKBOX,
    FIELD_TYPE_ENTITY_LIST
} from 'components/VirtualizedTable/constants';
import { SPRAY_EVENT_WEATHER_DROPDOWN_OPTIONS } from 'services/sprayEvents/constants';
import { SprayEventUuid } from 'services/sprayEvents/types';
import {
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager
} from 'services/usersMe/selectors';
import { selectSprayEventTypeIsCrop } from 'services/sprayEventType/selectors';
import { selectSprayMethodOptions } from 'components/SprayMethodDropdown/selectors';
import { getSprayEventApplicationTypeDisplay } from 'services/sprayEvents/utils';

const selectPeopleOptions = createDeepEqualSelector(
    selectPeopleKeyedById,
    (users: Record<Person['id'], Person>) =>
        formatDropdownOptions(users, {
            keyField: 'id',
            textField: 'fullName'
        })
);

const selectSprayEventItems = createDeepEqualSelector(
    [selectFilteredSprayEventsArray, selectEntities, selectPeopleOptions],
    (sprayEventsArrayState, entities, peopleOptions) =>
        sprayEventsArrayState.map((sprayEvent): VirtualizedTableItem => {
            const {
                entityId,
                startDate,
                sprayEvent: {
                    applicationType,
                    sprayEventOperations,
                    isFinalSpray
                }
            } = sprayEvent;

            const entityPathNames = get(entities, [entityId, 'pathNames']);

            return {
                ...sprayEvent,
                sprayEvent: {
                    ...sprayEvent.sprayEvent,
                    applicationType:
                        getSprayEventApplicationTypeDisplay(applicationType)
                },
                key: `${sprayEvent.uuid}`,
                startDateFormatted: moment(startDate, 'YYYY-MM-DD').format(
                    'MMM D'
                ),
                entityPathNames,
                operatorPeople: peopleOptions
                    .filter(
                        (people) =>
                            sprayEventOperations
                                ?.map((operator) => operator.operatorPersonId)
                                .includes(people.value as number)
                    )
                    .map((people) => people.text)
                    .join(', '),
                isFinalSpray
            };
        })
);

const makeSelectSprayEventItems = (): typeof selectSprayEventItems =>
    selectSprayEventItems;

const selectSprayEventsTableColumns = createDeepEqualSelector(
    [
        makeSelectTableColumnWidths(),
        selectSpraySchemaEntityTypeName,
        selectSpraySchemaInnerEntityTypePluralName,
        selectPeopleOptions,
        selectSprayMethodOptions,
        selectSprayEventTypeIsCrop
    ],
    (
        tableColumnWidths,
        spraySchemaEntityTypeName,
        spraySchemaInnerEntityTypePluralName,
        personOptions,
        sprayMethodOptions,
        sprayEventTypeIsCrop
    ): VirtualizedTableColumns => {
        const result: VirtualizedTableColumns = [
            {
                label: 'Date',
                value: 'startDateFormatted',
                width: 100,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: spraySchemaEntityTypeName || 'Area',
                value: 'entityPathNames',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: 'ApplicationType',
                value: 'sprayEvent.applicationType',
                width: 150,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: 'Applicator',
                value: 'operatorPeople',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: 'Total Area (Ha)',
                value: 'sprayEvent.sumTotalArea',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: 'isRequired'
            },
            {
                label: 'Number of Tanks',
                value: 'sprayEvent.numberOfTanks',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: 'isRequired'
            },
            {
                label: 'Spray Method',
                value: 'sprayEvent.sprayMethod',
                width: 150,
                type: FIELD_TYPE_DROPDOWN,
                validators: 'isRequired',
                options: sprayMethodOptions
            },
            {
                label: 'Technically responsible person',
                value: 'sprayEvent.technicalPersonName',
                width: 150,
                type: FIELD_TYPE_STRING
            },
            {
                label: 'Justification',
                value: 'sprayEvent.justification',
                width: 150,
                type: FIELD_TYPE_STRING
            },
            {
                label: spraySchemaInnerEntityTypePluralName || 'sprayed',
                value: 'sprayEvent.sprayEntities',
                width: 150,
                type: FIELD_TYPE_ENTITY_LIST,
                disableSorting: true
            },
            {
                label: 'Weather',
                value: 'sprayEvent.weather',
                width: 125,
                type: FIELD_TYPE_DROPDOWN,
                options: SPRAY_EVENT_WEATHER_DROPDOWN_OPTIONS
            },
            {
                label: 'Final spray',
                value: 'sprayEvent.isFinalSpray',
                width: 100,
                type: FIELD_TYPE_CONFIRMATION_CHECKBOX
            },
            {
                label: 'Completed',
                value: 'jobs[0].completedAt',
                width: 100,
                type: FIELD_TYPE_CONFIRMATION_CHECKBOX
            },
            {
                label: 'Submitted',
                value: 'sprayEvent.isSubmitted',
                width: 100,
                type: FIELD_TYPE_CONFIRMATION_CHECKBOX
            }
        ];

        const cropSprayOnlyColumns = [
            'sprayEvent.applicationType',
            'sprayEvent.weather',
            'sprayEvent.isFinalSpray',
            'sprayEvent.numberOfTanks',
            'sprayEvent.isSubmitted'
        ];

        const columns = result.filter(({ value }) => {
            if (sprayEventTypeIsCrop) {
                return true;
            }

            return !cropSprayOnlyColumns.includes(value);
        });

        return columns.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectSprayEventsTableIsSelectable = createDeepEqualSelector(
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager,
    (isAdmin, isManager, isAssistantManager): boolean =>
        Boolean(isAdmin || isManager || isAssistantManager)
);

const makeSelectSprayEventsTableIsSelectable =
    (): typeof selectSprayEventsTableIsSelectable =>
        selectSprayEventsTableIsSelectable;

const selectSelectedIncompleteSprayEventsUuids = createDeepEqualSelector(
    selectSprayEvents,
    selectActiveTableCellRowIds,
    (sprayEvents, uuids: SprayEventUuid[]) =>
        uuids.filter((uuid) =>
            Boolean(!get(sprayEvents, [uuid, 'jobs', 0, 'completedAt']))
        )
);

const makeSelectSelectedIncompleteSprayEventsUuids =
    (): typeof selectSelectedIncompleteSprayEventsUuids =>
        selectSelectedIncompleteSprayEventsUuids;

export {
    selectFilteredSprayEventsArray,
    selectSprayEventsTableColumns,
    makeSelectSprayEventItems,
    makeSelectSprayEventsTableIsSelectable,
    selectSelectedIncompleteSprayEventsUuids,
    makeSelectSelectedIncompleteSprayEventsUuids
};
